<template>
  <div v-if="visible" class="overlay-q" @click.self="$emit('cerrar')">
    <div class="modal-q" role="dialog" aria-modal="true">
    <h5 class="modal-title">Precio dinámico</h5>

    <!-- ✅ Body scrolleable -->
    <div class="modal-body-q">

      <div class="q-row q-mar-bottom">
        <div class="q-col">
          <label class="label-q">Precio normal (€)</label>
          <input
            type="number"
            class="input-q"
            :value="precioBase"
            disabled
          />
        </div>

        <div class="q-col">
          <label class="label-q">Precio rebajado (€)</label>
          <input class="input-q" type="number" v-model="local.precio_rebajado" />
          <p v-if="errorPrecioRebajado" class="err">{{ errorPrecioRebajado }}</p>
        </div>
      </div>

      <div class="q-row q-mar-bottom">
        <div class="q-col">
          <label class="label-q">Descuento predefinido (%)</label>
          <select class="tipo-venta-q" v-model="local.descuento_predefinido">
            <option value="">—</option>
            <option v-for="p in opts" :key="p" :value="p">{{ p }}%</option>
          </select>
        </div>

        <div class="q-col">
          <label class="label-q">Descuento manual (%)</label>
          <input class="input-q" type="number" v-model="local.descuento_manual" />
          <p v-if="errorDescuentoManual" class="err">{{ errorDescuentoManual }}</p>
        </div>
      </div>

      <div class="q-row q-mar-bottom">
        <div class="q-col">
          <label class="label-q">Límite de usos</label>
          <input class="input-q" type="number" min="0" step="1" v-model="local.limite_usos" />
          <small class="muted">Vacío = ilimitado. 0 = desactiva la rebaja (muestra precio normal).</small>
          <p v-if="errorLimiteUsos" class="err">{{ errorLimiteUsos }}</p>
        </div>
      </div>

      <!-- Vista previa -->
      <div v-if="precioBaseNum > 0" class="preview-q q-mar-top">
        <div class="preview-title">Vista previa</div>

        <div class="preview-price">
          <span v-if="mostrarRebaja" class="precio-normal">
            {{ precioNormalFmt }} €
          </span>

          <span class="precio-final" :class="{ 'solo-normal': !mostrarRebaja }">
            {{ precioFinalFmt }} €
          </span>
        </div>

        <div class="preview-meta">
          <span v-if="!local.activo" class="badge-off">Desactivado</span>
          <span v-else-if="mostrarRebaja" class="badge-on">Rebaja activa</span>
          <span v-else class="badge-neutral">Sin rebaja</span>

          <span class="meta-right" v-if="local.limite_usos">
            Límite: {{ local.limite_usos }} usos
          </span>
          <span class="meta-right" v-else>
            Ilimitado
          </span>
        </div>
      </div>

      <!-- Error general dentro del body -->
      <p v-if="error" class="err">{{ error }}</p>
    </div>

    <!-- ✅ Footer fijo (siempre visible) -->
    <div class="modal-footer-q">
      <div class="actions-q">
        <button type="button" class="btn-q-secondary" @click="$emit('cerrar')">
          Cerrar
        </button>

        <button type="button" class="btn-q-primary" @click="guardar" :disabled="hayErrores">
          Guardar
        </button>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
export default {
  name: "ModalPrecioDinamico",
  props: {
    visible: { type: Boolean, default: false },
    modelValue: { type: Object, required: true }, // tu v-model
    precioBase: { type: [String, Number], default: "" }, // 👈 nuevo
  },
  emits: ["update:modelValue", "cerrar"],

  data() {
    return {
      opts: [5,10,20,30,40,50,60,70,80,90],
      local: this.normalizar(this.modelValue),
      error: "",
    };
  },
    computed: {
        precioBaseNum() {
            const p = Number(String(this.precioBase ?? "").replace(",", "."));
            return Number.isFinite(p) ? p : 0;
        },

        // ✅ ¿hay rebaja elegida? (solo una debería estar activa por tu validación)
        tieneRebajaElegida() {
            const predef = this.local.descuento_predefinido !== "" && this.local.descuento_predefinido !== null;
            const manual = this.local.descuento_manual !== "" && this.local.descuento_manual !== null;
            const rebajado = this.local.precio_rebajado !== "" && this.local.precio_rebajado !== null;
            return predef || manual || rebajado;
        },

        // ✅ precio final calculado
        precioFinalNum() {
            const normal = this.precioBaseNum;

            // Si está desactivado, NO se muestra rebaja aunque exista configuración
            if (!this.local.activo) return normal;

            // Prioridad: precio rebajado > descuento manual > descuento predefinido
            const pr = Number(String(this.local.precio_rebajado ?? "").replace(",", "."));
            if (Number.isFinite(pr) && pr > 0) return pr;

            const dm = Number(String(this.local.descuento_manual ?? "").replace(",", "."));
            if (Number.isFinite(dm) && dm > 0) return +(normal * (1 - dm / 100)).toFixed(2);

            const dp = Number(String(this.local.descuento_predefinido ?? "").replace(",", "."));
            if (Number.isFinite(dp) && dp > 0) return +(normal * (1 - dp / 100)).toFixed(2);

            return normal;
        },

        // ✅ mostrar tachado solo si hay rebaja real y el final < normal
        mostrarRebaja() {
            return this.local.activo && this.tieneRebajaElegida && this.precioFinalNum < this.precioBaseNum;
        },

        precioNormalFmt() {
            return this.precioBaseNum.toFixed(2);
        },
        precioFinalFmt() {
            return Number(this.precioFinalNum || 0).toFixed(2);
        },
        precioRebajadoNum() {
          const n = Number(String(this.local.precio_rebajado ?? "").replace(",", "."));
          return Number.isFinite(n) ? n : null;
        },

        descuentoManualNum() {
          const n = Number(String(this.local.descuento_manual ?? "").replace(",", "."));
          return Number.isFinite(n) ? n : null;
        },

        limiteUsosNum() {
          const n = Number(String(this.local.limite_usos ?? "").replace(",", "."));
          return Number.isFinite(n) ? n : null;
        },

        errorPrecioRebajado() {
          if (this.local.precio_rebajado === "" || this.local.precio_rebajado === null) return "";
          const pr = this.precioRebajadoNum;
          if (pr === null) return "Precio rebajado inválido.";
          if (pr <= 0) return "El precio rebajado debe ser mayor que 0.";
          if (pr >= this.precioBaseNum) return "El precio rebajado debe ser menor que el precio normal.";
          return "";
        },

        errorDescuentoManual() {
          if (this.local.descuento_manual === "" || this.local.descuento_manual === null) return "";
          const dm = this.descuentoManualNum;
          if (dm === null) return "Descuento manual inválido.";
          if (dm < 0) return "El descuento manual no puede ser negativo.";
          if (dm >= 100) return "El descuento manual debe ser menor que 100%.";
          return "";
        },

        errorLimiteUsos() {
          if (this.local.limite_usos === "" || this.local.limite_usos === null) return "";
          const lu = this.limiteUsosNum;
          if (lu === null) return "Límite de usos inválido.";
          if (!Number.isInteger(lu)) return "El límite de usos debe ser un entero.";
          if (lu < 0) return "El límite de usos no puede ser menor que 0.";
          return "";
        },


        hayErrores() {
          return !!(this.errorPrecioRebajado || this.errorDescuentoManual || this.errorLimiteUsos);
        }
    },


  watch: {
  
    modelValue: {
      deep: true,
      handler(v) {
        this.local = this.normalizar(v);
      },
    },
    visible(n) {
        if (n) {
            this.local = this.normalizar(this.modelValue);
            // 🔥 precio normal SIEMPRE viene del alta (padre)
            this.local.precio_normal = this.precioBaseNum > 0 ? String(this.precioBaseNum) : "";
        }
        this.error = "";
    },
        // ✅ SOLO UNA OPCIÓN ACTIVA: si rellenas uno, limpia los otros
      "local.precio_rebajado"(v) {
      const n = Number(String(v ?? "").replace(",", "."));
      const tieneAlgo = Number.isFinite(n) && n > 0;

      if (tieneAlgo) {
        this.local.descuento_predefinido = "";
        this.local.descuento_manual = "";
        this.error = "";
      }
    },

    "local.descuento_predefinido"(v) {
      const tieneAlgo = v !== "" && v !== null;

      if (tieneAlgo) {
        this.local.precio_rebajado = "";
        this.local.descuento_manual = "";
        this.error = "";
      }
    },

    "local.descuento_manual"(v) {
      const n = Number(String(v ?? "").replace(",", "."));
      const tieneAlgo = Number.isFinite(n) && n > 0;

      if (tieneAlgo) {
        this.local.precio_rebajado = "";
        this.local.descuento_predefinido = "";
        this.error = "";
      }
    },


    
  },
  methods: {
    normalizar(v) {
      return {
        precio_normal: v?.precio_normal ?? "",
        descuento_predefinido: v?.descuento_predefinido ?? "",
        descuento_manual: v?.descuento_manual ?? "",
        precio_rebajado: v?.precio_rebajado ?? "",
        limite_usos: v?.limite_usos ?? "",
        activo: v?.activo ?? true,
      };
    },
    guardar() {
      // 0) Precio base debe ser válido
      if (!this.precioBaseNum || this.precioBaseNum <= 0) {
        this.error = "Primero debes ingresar un precio válido en el alta del servicio.";
        return;
      }

      // 1) Validaciones por campo
      if (this.errorPrecioRebajado) {
        this.error = this.errorPrecioRebajado;
        return;
      }
      if (this.errorDescuentoManual) {
        this.error = this.errorDescuentoManual;
        return;
      }
      if (this.errorLimiteUsos) {
        this.error = this.errorLimiteUsos;
        return;
      }

      // 2) Regla “solo una opción” (extra por seguridad)
      const tienePredef = this.local.descuento_predefinido !== "" && this.local.descuento_predefinido !== null;
      const tieneManual = this.local.descuento_manual !== "" && this.local.descuento_manual !== null;
      const tieneRebajado = this.local.precio_rebajado !== "" && this.local.precio_rebajado !== null;

      const count = [tienePredef, tieneManual, tieneRebajado].filter(Boolean).length;
      if (count > 1) {
        this.error = "Usa solo UNA opción: descuento predefinido, descuento manual o precio rebajado.";
        return;
      }

      // 3) Payload final
      const payload = {
        ...this.local,
        precio_normal: String(this.precioBaseNum),
      };

      this.error = "";
      this.$emit("update:modelValue", payload);
      this.$emit("cerrar");
    }
  },
};
</script>

<style scoped>

/* =========================
   Overlay
========================= */
.overlay-q{
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 25, 0.55);
  backdrop-filter: blur(7px);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index: 9999;
  padding: 14px;
  overflow-y: auto; /* 🔥 importante en móvil */
  -webkit-overflow-scrolling: touch; 
}

/* =========================
   Modal
========================= */
.modal-q{
  width: 100%;
  max-width: 720px;

  /* ✅ clave: no ocupar toda la pantalla */
  max-height: min(86vh, 680px);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* ✅ el scroll será interno */

  background: var(--q-card);
  border-radius: 18px;
  padding: 18px;
  border: 1px solid var(--q-border-md);
  box-shadow: 0 18px 55px rgba(0,0,0,0.5);
  animation: popIn .18s ease-out;
}

@keyframes popIn {
  from { transform: translateY(10px) scale(0.985); opacity: 0; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
}

/* =========================
   Header
========================= */
.modal-title{
  margin: 0 0 14px 0;
  font-weight: 900;
  color: var(--q-text);
  letter-spacing: .2px;
}

/* =========================
   Layout
========================= */
.q-row{
  display:flex;
  gap: 14px;
  align-items:flex-end;
  flex-wrap: wrap;
}

.q-col{
  flex: 1;
  min-width: 240px;
}

.q-mar-bottom{ margin-bottom: 14px; }
.q-mar-top{ margin-top: 14px; }

.label-q{
  display:block;
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--q-text);
  margin-bottom: 6px;
}

/* =========================
   Inputs
========================= */
.input-q,
.tipo-venta-q{
  width: 100%;
  height: 44px;
  background: var(--q-hover);
  border: 1px solid var(--q-border-md);
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 0.95rem;
  color: var(--q-text);
  transition: 0.15s ease;
  outline: none;
}

.input-q:focus,
.tipo-venta-q:focus{
  border-color: var(--q-gold);
  box-shadow: 0 0 0 3px rgba(245,166,35,0.22);
}

.muted{
  font-size: 0.82rem;
  color: var(--q-muted);
  display:block;
  margin-top: 6px;
  font-weight: 600;
}

/* =========================
   Preview
========================= */
.preview-q{
  border-radius: 16px;
  padding: 14px;
  border: 1px solid var(--q-border-md);
  background: var(--q-hover);
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  text-align: center;
}

.preview-title{
  font-weight: 900;
  color: var(--q-text);
  margin-bottom: 10px;
}

.preview-price{
  display:flex;
  justify-content:center;
  align-items: baseline;
  gap: 12px;
}

.precio-normal{
  color: var(--q-red);
  text-decoration: line-through;
  font-weight: 900;
  font-size: 1.05rem;
}

.precio-final{
  color: var(--q-green);
  font-weight: 950;
  font-size: 1.7rem;
  letter-spacing: .3px;
}

.precio-final.solo-normal{
  color: var(--q-text);
}

.preview-meta{
  margin-top: 10px;
  display:flex;
  justify-content:center;
  gap: 12px;
  flex-wrap: wrap;
}

.badge-on, .badge-off, .badge-neutral{
  font-size: 0.78rem;
  font-weight: 900;
  padding: 6px 10px;
  border-radius: 999px;
}

.badge-on{
  background: var(--q-green-soft);
  color: var(--q-green);
}

.badge-off{
  background: var(--q-red-soft);
  color: var(--q-red);
}

.badge-neutral{
  background: var(--q-amber-soft);
  color: var(--q-gold);
}

/* =========================
   Buttons
========================= */
.actions-q{
  display:flex;
  gap: 12px;
  margin-top: 18px;
}

.btn-q-primary,
.btn-q-secondary{
  height: 46px;
  border-radius: 12px;
  padding: 0 18px;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;
  transition: .15s ease;
  border: none;
  flex: 1;
}

.btn-q-primary{
  background: var(--q-gold);
  color: var(--q-gold-on);
  box-shadow: 0 10px 22px rgba(245,166,35,0.25);
}

.btn-q-primary:active{
  transform: scale(0.98);
}

.btn-q-secondary{
  background: var(--q-hover);
  color: var(--q-text);
  border: 1px solid var(--q-border-md);
}

.err{
  margin-top: 10px;
  color: var(--q-red);
  font-size:0.85rem;
  font-weight: 700;
}

/* =========================
   Mobile Improvements
========================= */
@media (max-width: 768px){

  .overlay-q{
    align-items: center; /* ✅ card centrada */
    padding: 12px;
  }

  .modal-q{
    max-height: 82vh;      /* ✅ nunca tapa botones */
    border-radius: 18px;   /* ✅ sigue como card */
    padding: 16px;
  }

  .q-col{
    min-width: 100%;
  }

  .actions-q{
    flex-direction: column;
    gap: 12px;
  }

  .btn-q-primary,
  .btn-q-secondary{
    height: 64px;          /* 🔥 más altos */
    min-height: 64px;      /* ✅ asegura altura real en iOS */
    font-size: 1.08rem;
    border-radius: 16px;

    display: flex;         /* ✅ centra texto perfecto */
    align-items: center;
    justify-content: center;
    line-height: 1;        /* ✅ evita efecto “línea” */
  }
}
.modal-body-q{
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 4px;
}

.modal-footer-q{
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--q-border);
  background: linear-gradient(180deg, transparent 0%, var(--q-card) 35%, var(--q-card) 100%);
}
</style>
